// web-commands.ts — the PER-APP half of the web shape: maps oriora_curio's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. The one derived command (compute_signals) is the app's
// "paid draw" computed in signals.rs — on web it returns a minimal shell for now; a faithful web
// build reimplements that signal logic in JS (the known per-app wrinkle, see MACOS/web docs).

import type { CommandHandler, WebStore, Row } from "./web-runtime";
import { computeSignalsWeb } from "./signals-web";
import type { Item, CollectionSet } from "./store";
import { browserSearch, browserFetch } from "./web-search-browser";

const COLLECTIONS = "collections";
const ITEMS = "items";
const SETS = "sets";
const PROVENANCE = "provenance_events";

function uuid(): string {
  // crypto.randomUUID is available in every modern browser.
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5,
        undefined, args.mode === "open_web_first"),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Collections ──────────────────────────────────────────────────────────
    list_collections: () =>
      db.all(COLLECTIONS).sort((a, b) =>
        String((a as { created_at?: string }).created_at ?? "").localeCompare(
          String((b as { created_at?: string }).created_at ?? ""),
        ),
      ),
    get_collection: ({ id }) => db.get(COLLECTIONS, String(id)),
    create_collection: ({ input }) => {
      const i = input as Record<string, unknown>;
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        name: String(i.name ?? "").trim(),
        kind: String(i.kind ?? "").trim(),
        attr_schema: Array.isArray(i.attr_schema) ? i.attr_schema : [],
        notes: String(i.notes ?? "").trim(),
        created_at: now,
        updated_at: now,
      };
      return db.insert(COLLECTIONS, row);
    },
    update_collection: ({ id, patch }) => {
      db.update(COLLECTIONS, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    delete_collection: ({ id }) => {
      // Rust CASCADEs the collection's items + sets on delete — mirror it here.
      const cid = String(id);
      for (const it of db.all(ITEMS).filter((r) => (r as { collection_id?: string }).collection_id === cid)) {
        db.remove(ITEMS, it.id);
      }
      for (const s of db.all(SETS).filter((r) => (r as { collection_id?: string }).collection_id === cid)) {
        db.remove(SETS, s.id);
      }
      db.remove(COLLECTIONS, cid);
      return null;
    },

    // ── Sets ─────────────────────────────────────────────────────────────────
    list_sets: ({ collectionId }) =>
      db
        .all(SETS)
        .filter((r) => (r as { collection_id?: string }).collection_id === String(collectionId))
        .sort((a, b) =>
          String((a as { created_at?: string }).created_at ?? "").localeCompare(
            String((b as { created_at?: string }).created_at ?? ""),
          ),
        ),
    create_set: ({ input }) => {
      const i = input as Record<string, unknown>;
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        collection_id: String(i.collection_id ?? ""),
        name: String(i.name ?? "").trim(),
        target_size: (i.target_size as number | null | undefined) ?? null,
        notes: String(i.notes ?? "").trim(),
        created_at: now,
        updated_at: now,
      };
      return db.insert(SETS, row);
    },
    update_set: ({ id, patch }) => {
      db.update(SETS, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    delete_set: ({ id }) => {
      // Detach members (don't delete the items), then drop the set — mirrors db.rs.
      const sid = String(id);
      for (const it of db.all(ITEMS).filter((r) => (r as { set_id?: string }).set_id === sid)) {
        db.update(ITEMS, it.id, { set_id: "" });
      }
      db.remove(SETS, sid);
      return null;
    },

    // ── Items ────────────────────────────────────────────────────────────────
    list_items: ({ collectionId, limit }) => {
      let rows = db.all(ITEMS);
      if (collectionId) rows = rows.filter((r) => (r as { collection_id?: string }).collection_id === String(collectionId));
      rows = rows.sort((a, b) =>
        String((b as { created_at?: string }).created_at ?? "").localeCompare(
          String((a as { created_at?: string }).created_at ?? ""),
        ),
      );
      const lim = typeof limit === "number" ? limit : 1_000_000;
      return rows.slice(0, lim);
    },
    get_item: ({ id }) => db.get(ITEMS, String(id)),
    create_item: ({ input }) => {
      const i = input as Record<string, unknown>;
      const now = nowIso();
      const row: Row = {
        id: uuid(),
        collection_id: String(i.collection_id ?? ""),
        name: String(i.name ?? "").trim(),
        photo_ref: String(i.photo_ref ?? "").trim(),
        acquired_on: String(i.acquired_on ?? "").trim(),
        source: String(i.source ?? "").trim(),
        cost: (i.cost as number | null | undefined) ?? null,
        condition: String(i.condition ?? "").trim(),
        attrs: i.attrs && typeof i.attrs === "object" ? i.attrs : {},
        wishlist: Boolean(i.wishlist ?? false),
        notes: String(i.notes ?? "").trim(),
        set_id: String(i.set_id ?? "").trim(),
        created_at: now,
        updated_at: now,
      };
      return db.insert(ITEMS, row);
    },
    update_item: ({ id, patch }) => {
      db.update(ITEMS, String(id), { ...(patch as Record<string, unknown>), updated_at: nowIso() });
      return null;
    },
    delete_item: ({ id }) => {
      const iid = String(id);
      // Rust CASCADEs an item's provenance_events on delete — mirror it here.
      for (const ev of db.all(PROVENANCE).filter((r) => (r as { item_id?: string }).item_id === iid)) {
        db.remove(PROVENANCE, ev.id);
      }
      db.remove(ITEMS, iid);
      return null;
    },

    // ── Provenance events (per item) ────────────────────────────────────────
    list_provenance_events: ({ itemId }) =>
      db
        .all(PROVENANCE)
        .filter((r) => (r as { item_id?: string }).item_id === String(itemId))
        .sort((a, b) => {
          const ao = String((a as { occurred_on?: string }).occurred_on ?? "");
          const bo = String((b as { occurred_on?: string }).occurred_on ?? "");
          if (ao !== bo) return ao.localeCompare(bo);
          return String((a as { created_at?: string }).created_at ?? "").localeCompare(
            String((b as { created_at?: string }).created_at ?? ""),
          );
        }),
    create_provenance_event: ({ input }) => {
      const i = input as Record<string, unknown>;
      const row: Row = {
        id: uuid(),
        item_id: String(i.item_id ?? ""),
        occurred_on: String(i.occurred_on ?? "").trim(),
        kind: String(i.kind ?? "").trim() || "note",
        text: String(i.text ?? "").trim(),
        created_at: nowIso(),
      };
      return db.insert(PROVENANCE, row);
    },
    delete_provenance_event: ({ id }) => {
      db.remove(PROVENANCE, String(id));
      return null;
    },

    // ── Derived signals — Rust in signals.rs, faithfully ported for web in signals-web.ts. ──
    // This used to be a valid-but-EMPTY shell, which meant the web build showed nothing derived
    // AND the one billed narration call was handed a catalogue of zeros.
    compute_signals: ({ collectionId, today }) => {
      const col = db.get(COLLECTIONS, String(collectionId)) as Record<string, unknown> | null;
      // Newest-first, matching list_items — recent_additions relies on that order.
      const items = (db.all(ITEMS) as Row[])
        .filter((r) => String((r as { collection_id?: string }).collection_id ?? "") === String(collectionId))
        .sort((a, b) => String((b as { created_at?: string }).created_at ?? "").localeCompare(String((a as { created_at?: string }).created_at ?? "")));
      const sets = (db.all(SETS) as Row[])
        .filter((r) => String((r as { collection_id?: string }).collection_id ?? "") === String(collectionId));
      return computeSignalsWeb(
        String(col?.name ?? ""),
        String(col?.kind ?? ""),
        col?.attr_schema,
        items as unknown as Item[],
        sets as unknown as CollectionSet[],
        String(today ?? new Date().toISOString().slice(0, 10)),
      );
    },
  };
}
